import { motion } from 'motion/react';
import { FC, ReactElement, useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useSearchParams } from 'react-router-dom';
import LogoIcon from '../Icons/LogoIcon';
import { elementsTransition } from '@/utils/gamePacks';
import LeftFixedShape from '../Host/Setup/BackgroundElements/LeftFixedShape';
import BottomLineElement from '../Host/Setup/BackgroundElements/BottomLineElement';
import CenterFixedShape from '../Host/Setup/BackgroundElements/CenterFixedShape';
import SettingsIcon from '../Icons/SettingsIcon';
import { useAuth } from '@/providers/AuthProvider';
import ChevronSmLeftIcon from '../Icons/ChevronSmLeftIcon';
import FreeTrialBanner from '../FreeTrial/FreeTrialBanner';

const GuideLayout: FC<{
  hideFreeTrialBanner?: boolean;
}> = ({ hideFreeTrialBanner = false }): ReactElement => {
  const location = useLocation();
  const { updateUserAttributes, userAttributes } = useAuth();
  const [isGuide, setIsGuide] = useState<boolean>(false);

  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.has('guide') || location.pathname.indexOf('guide') > -1) {
      setIsGuide(true);
    }
  }, []);

  useEffect(() => {
    if (!userAttributes || userAttributes['custom:visited_guide'] === '1') return;

    updateUserAttributes({
      userAttributes: {
        'custom:visited_guide': '1'
      }
    });
  }, [userAttributes]);

  return (
    <motion.div
      className="relative min-h-screen w-full"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="h-full w-full absolute z-0 overflow-auto bg-secondary-clementine-100">
        {isGuide ? (
          <div className="h-64 py-10 mx-32 border-b border-neutral-200 flex items-center justify-between relative">
            <Link to="/dashboard" className="cursor-pointer">
              <LogoIcon className="w-80 ml-18" />
            </Link>

            <h3 className="text-h3 text-neutral-400">Game Setup</h3>
            <Link to="/dashboard/profile" className="cursor-pointer mr-12 w-24 ml-62">
              <SettingsIcon />
            </Link>
          </div>
        ) : (
          <div className="h-64 py-10 mx-32 border-b border-neutral-200 z-10 flex justify-between items-center">
            <Link to="/dashboard" className="link link-game py-9">
              <ChevronSmLeftIcon />
              <span>Back To Home</span>
            </Link>
            {!hideFreeTrialBanner && <FreeTrialBanner smallVersion />}
          </div>
        )}
        <LeftFixedShape
          className="absolute top-135 left-0 origin-left"
          initial={{ scale: 0.5 }}
          animate={{ scale: 1 }}
          transition={elementsTransition}
        />

        <BottomLineElement
          className="absolute bottom-0 left-1/2 -translate-x-1/2 origin-bottom"
          initial={{ scale: 0.5 }}
          animate={{ scale: 1 }}
          transition={elementsTransition}
        />
        <div className="flex justify-center mt-56">
          <motion.div
            initial={{ opacity: 0, x: 150 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -150 }}
            key={location.pathname}
            className="w-full max-h-640 relative"
          >
            <CenterFixedShape
              className={`absolute z-0 top-360 left-1/2`}
              initial={{ scale: 0.5 }}
              animate={{ scale: 1 }}
              transition={elementsTransition}
            />
            <Outlet />
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export default GuideLayout;
